<template>
  <div class="express-pay-page">
    <button type="button" class="lang-btn" @click="toggleLang">
      <global-outlined />
      {{ lang === 'zh' ? 'English' : '中文' }}
    </button>

    <!-- PC -->
    <div v-if="!isMobile" class="pc-panel">
      <div class="pc-card">
        <div class="warn-box">
          {{ t.warn }}
        </div>
        <div class="qr-wrap">
          <canvas ref="qrCanvas" class="qr-canvas" />
          <div class="qr-usdt">
            <span class="usdt-icon">₮</span>
          </div>
        </div>
        <div class="order-rows">
          <div v-for="row in orderRows" :key="row.label" class="order-row">
            <span class="row-label">{{ row.label }}</span>
            <span class="row-value">{{ row.value }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile -->
    <div v-else class="mobile-panel">
      <div class="mobile-hero">
        <div class="lock-icon">
          <lock-outlined />
        </div>
        <p class="hero-text">{{ t.safeTip }}</p>
      </div>

      <div class="warn-box mobile-warn">
        {{ t.warn }}
      </div>

      <div class="mobile-order-card">
        <p class="card-title">{{ t.orderInfo }}</p>
        <div class="m-row">
          <span class="m-label">{{ t.orderNo }}</span>
          <span class="m-value">{{ detail.orderNo }}</span>
        </div>
        <div class="m-row">
          <span class="m-label">{{ t.productDetail }}</span>
          <span class="m-value">{{ detail.title }}</span>
        </div>
        <div class="m-row">
          <span class="m-label">{{ t.qty }}</span>
          <span class="m-value">{{ detail.inputNumber }}</span>
        </div>
        <div class="m-divider" />
        <div class="m-row m-amount-row">
          <span class="m-label">{{ t.payAmount }}</span>
          <span class="m-amount">{{ payAmount }}</span>
        </div>
        <p class="card-foot">{{ t.orderInfo }}</p>
      </div>

      <div class="wallet-section">
        <p class="wallet-title">
          <span class="title-bar" />
          {{ t.selectWallet }}
        </p>
        <div class="wallet-grid">
          <div
            v-for="item in payList"
            :key="item.value"
            class="wallet-item"
            :class="{ active: checkedVal === item.value }"
            @click="checkedVal = item.value"
          >
            <span class="wallet-radio" :class="{ checked: checkedVal === item.value }" />
            <img :src="item.src" :alt="item.title" class="wallet-logo" />
            <span class="wallet-name">{{ item.title }}</span>
          </div>
        </div>
      </div>

      <div class="mobile-pay-bar">
        <button type="button" class="open-pay-btn" @click="onPay">
          💳 {{ t.openPay }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import { message } from 'ant-design-vue';
import { GlobalOutlined, LockOutlined } from '@ant-design/icons-vue';

const PAY_HOST = 'https://client.uekcdt.top';

const TEXT = {
  zh: {
    warn: '付款成功后请勿关闭此页面，请耐心等待自动回调订单，否则可能无法自动到账！',
    safeTip: '为了您的安全起见，请打开钱包App进行支付！',
    orderInfo: '订单信息',
    orderNo: '商品订单',
    productDetail: '商品详情',
    qty: '购买数量',
    payAmount: '付款金额',
    orderExtra: '订单信息',
    selectWallet: '选择付款钱包',
    openPay: '打开支付',
    pickWallet: '请先选择付款钱包',
  },
  en: {
    warn: 'Do not close this page after payment. Wait for the order callback.',
    safeTip: 'For your security, please open your wallet app to pay.',
    orderInfo: 'Order Info',
    orderNo: 'Order No.',
    productDetail: 'Product',
    qty: 'Quantity',
    payAmount: 'Amount',
    orderExtra: 'Order Info',
    selectWallet: 'Select Wallet',
    openPay: 'Open Payment',
    pickWallet: 'Please select a wallet first',
  },
};

export default {
  name: 'ExpressPayment',
  components: { GlobalOutlined, LockOutlined },
  data() {
    return {
      isMobile: false,
      lang: 'zh',
      detail: {},
      referralCode: '',
      checkedVal: '',
      payList: [
        { title: 'imToken钱包', src: require('../assets/imtoken.jpg'), value: 'imtoken' },
        { title: 'TokenPocket', src: require('../assets/tokenpocket.jpg'), value: 'tokenpocket' },
        { title: 'BitGet钱包', src: require('../assets/bitget.jpg'), value: 'bitget' },
        { title: 'TronLink钱包', src: require('../assets/tronlink.jpg'), value: 'tronlink' },
      ],
    };
  },
  computed: {
    t() {
      return TEXT[this.lang] || TEXT.zh;
    },
    payAmount() {
      const p = Number(this.detail.price) || 0;
      const n = Number(this.detail.inputNumber) || 1;
      const total = p * n;
      return total % 1 === 0 ? total.toFixed(2) : total.toFixed(2);
    },
    orderInfoText() {
      if (this.detail.tgAccount) {
        return this.detail.tgAccount;
      }
      const parts = [
        this.detail.receiveAddress,
        this.detail.receiverName,
        this.detail.receivePhone,
      ].filter(Boolean);
      return parts.join(' / ') || '';
    },
    orderRows() {
      return [
        { label: this.t.orderNo, value: this.detail.orderNo || '—' },
        { label: this.t.productDetail, value: this.detail.title || '—' },
        { label: this.t.qty, value: String(this.detail.inputNumber ?? '—') },
        { label: this.t.payAmount, value: this.payAmount },
        { label: this.t.orderExtra, value: this.orderInfoText },
      ];
    },
    walletPayUrl() {
      const orderNo = encodeURIComponent(this.detail.orderNo || '');
      const code = encodeURIComponent(this.referralCode || '');
      return `${PAY_HOST}/walletPay/tgNo/${orderNo}/referralCode/${code}`;
    },
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);

    try {
      const data = JSON.parse(this.$route.params.data);
      this.detail = data;
      this.referralCode = data.referral_code || '';
    } catch (e) {
      console.error('ExpressPayment: invalid route data', e);
      message.error('订单数据无效');
      return;
    }

    if (!this.isMobile) {
      this.$nextTick(() => this.renderQr());
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  watch: {
    isMobile(val, oldVal) {
      if (!val && oldVal) {
        this.$nextTick(() => this.renderQr());
      }
    },
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    toggleLang() {
      this.lang = this.lang === 'zh' ? 'en' : 'zh';
    },
    renderQr() {
      const canvas = this.$refs.qrCanvas;
      if (!canvas) return;
      QRCode.toCanvas(canvas, this.walletPayUrl, { width: 220, margin: 1 }, (err) => {
        if (err) console.error(err);
      });
    },
    onPay() {
      if (!this.checkedVal) {
        message.warning(this.t.pickWallet);
        return;
      }
      const url = this.walletPayUrl;

      if (this.checkedVal === 'imtoken') {
        window.location.href = `imtokenv2://navigate/DappView?url=${encodeURIComponent(url)}`;
      } else if (this.checkedVal === 'tokenpocket') {
        const param = encodeURIComponent(
          JSON.stringify({ url, chain: 'Tron', source: 'shopSer' })
        );
        window.location.href = `tpdapp://open?params=${param}`;
      } else if (this.checkedVal === 'bitget') {
        window.location.href = `https://bkcode.vip?action=dapp&url=${encodeURIComponent(url)}`;
      } else if (this.checkedVal === 'tronlink') {
        window.location.href = `tronlinkapp://open?url=${encodeURIComponent(url)}`;
      } else {
        window.location.href = url;
      }
    },
  },
};
</script>

<style scoped lang="less">
@blue: #1890ff;
@bg: linear-gradient(180deg, #c5e3f6 0%, #e8f4fc 50%, #f2f8fc 100%);

.express-pay-page {
  min-height: 100vh;
  background: @bg;
  position: relative;
  padding: 24px 16px 100px;
}

.lang-btn {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: none;
  border-radius: 20px;
  background: @blue;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.35);
}

.warn-box {
  background: #fff3f3;
  color: #e7473c;
  font-size: 14px;
  line-height: 1.6;
  padding: 12px 16px;
  border-radius: 8px;
  text-align: center;
}

/* —— PC —— */
.pc-panel {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 48px);
  padding-top: 40px;
}

.pc-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(100, 150, 200, 0.2);
  padding: 24px 28px 28px;
}

.qr-wrap {
  position: relative;
  display: flex;
  justify-content: center;
  margin: 24px 0;
}

.qr-canvas {
  display: block;
  border-radius: 4px;
}

.qr-usdt {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 44px;
  height: 44px;
  background: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 4px #fff;
}

.usdt-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #26a17b;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.order-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.order-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 48px;
  padding: 0 16px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  font-size: 14px;
}

.row-label {
  font-weight: 600;
  color: #333;
  flex-shrink: 0;
  margin-right: 12px;
}

.row-value {
  color: #333;
  text-align: right;
  word-break: break-all;
}

/* —— Mobile —— */
.mobile-panel {
  max-width: 480px;
  margin: 48px auto 0;
}

.mobile-hero {
  text-align: center;
  margin-bottom: 16px;
}

.lock-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  background: @blue;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #ffd666;
}

.hero-text {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #222;
  line-height: 1.5;
  padding: 0 8px;
}

.mobile-warn {
  margin-bottom: 16px;
  background: #fffbe6;
  border: 1px solid #f0d78c;
  color: #ad6800;
  font-size: 13px;
}

.mobile-order-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px 18px 12px;
  box-shadow: 0 4px 16px rgba(100, 150, 200, 0.12);
  margin-bottom: 20px;
}

.card-title {
  margin: 0 0 14px;
  font-size: 15px;
  font-weight: 600;
  color: @blue;
}

.m-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
  font-size: 14px;
  gap: 12px;
}

.m-label {
  color: @blue;
  font-weight: 500;
  flex-shrink: 0;
}

.m-value {
  color: #333;
  text-align: right;
  word-break: break-all;
}

.m-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 12px 0;
}

.m-amount-row {
  margin-bottom: 8px;
}

.m-amount {
  font-size: 22px;
  font-weight: 700;
  color: @blue;
}

.card-foot {
  margin: 8px 0 0;
  font-size: 12px;
  color: #91caff;
}

.wallet-section {
  margin-bottom: 24px;
}

.wallet-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  font-size: 15px;
  font-weight: 600;
  color: #333;

  .title-bar {
    width: 4px;
    height: 18px;
    background: @blue;
    border-radius: 2px;
  }
}

.wallet-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.wallet-item {
  position: relative;
  background: #fff;
  border-radius: 12px;
  padding: 28px 12px 14px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(100, 150, 200, 0.1);
  border: 2px solid transparent;
  cursor: pointer;

  &.active {
    border-color: @blue;
  }
}

.wallet-radio {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 18px;
  height: 18px;
  border: 2px solid #d9d9d9;
  border-radius: 50%;

  &.checked {
    border-color: @blue;

    &::after {
      content: '';
      position: absolute;
      top: 3px;
      left: 3px;
      width: 8px;
      height: 8px;
      background: @blue;
      border-radius: 50%;
    }
  }
}

.wallet-logo {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: contain;
  display: block;
  margin: 0 auto 8px;
}

.wallet-name {
  font-size: 13px;
  color: #333;
}

.mobile-pay-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 16px 20px;
  background: linear-gradient(180deg, transparent, #e8f4fc 30%);
}

.open-pay-btn {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  display: block;
  height: 48px;
  border: none;
  border-radius: 24px;
  background: @blue;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(24, 144, 255, 0.4);
}

@media (min-width: 769px) {
  .express-pay-page {
    padding-bottom: 24px;
  }
}
</style>
